/**
 * ווידג'ט CAPTCHA — TASKS 4.3 · PRD 12.2. Turnstile או hCaptcha, לפי
 * `VITE_CAPTCHA_PROVIDER` (⚠️ חייב לתאום ל-`CAPTCHA_PROVIDER` בשרת).
 *
 * ⚠️ **מפתחות ה-CAPTCHA הם per-domain** — ב-dev בלי `VITE_CAPTCHA_SITEKEY`
 *    אמיתי הווידג'ט ⛔ אינו נטען, וזה תקין: הוא לא חוסם פיתוח מקומי, רק
 *    מונע שליחה אמיתית בלי טוקן. ⭐ ב-dev/E2E משתמשים במפתחות הבדיקה
 *    **הרשמיים** של Cloudflare ("עובר תמיד") — ראו `.env.example`.
 *
 * 🔴 **שני באגים אמיתיים שנתפסו רק בדפדפן אמיתי (Playwright MCP), ⛔ לא
 *    ב-jsdom/vitest — הווידג'ט מעולם לא נרנדר, בלי שום שגיאת console:**
 *
 *    1. **מרוץ אמיתי באירוע `onload` של תג ה-`<script>`.** `onload` יורה
 *       ברגע שהקובץ הורץ — ⛔ **לא** ברגע ש-`window.turnstile` מוכן בפועל
 *       (Turnstile טוען משאבים פנימיים אסינכרוניים אחרי הרצת הסקריפט הראשי).
 *       אומת ישירות: `api` היה `false` בדיוק ברגע ה-`.then()`. ⭐ **הפתרון
 *       המתועד של Cloudflare/hCaptcha:** callback בשם מוגדר מראש דרך פרמטר
 *       `onload=` ב-URL של הסקריפט עצמו — הספק קורא לו כשה-API באמת מוכן.
 *    2. **תלות ב-`[onVerify, onExpire]`** — שני callbacks שמגיעים כ-arrow
 *       functions inline מ-`RequestPage`, ולכן מקבלים זהות חדשה בכל רינדור.
 *       ⇒ ה-effect התפרק והופעל מחדש לפני שהגיע ל-`.render()`. ⭐ הפתרון:
 *       callbacks ב-ref, וה-effect תלוי רק בזהות היציבה של `containerRef`.
 */
import { useEffect, useRef } from 'react';

function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

type CaptchaProvider = 'turnstile' | 'hcaptcha';

const SITE_KEY = import.meta.env.VITE_CAPTCHA_SITEKEY as string | undefined;
const PROVIDER =
  (import.meta.env.VITE_CAPTCHA_PROVIDER as CaptchaProvider | undefined) ?? 'turnstile';

const SCRIPT_BASE: Record<CaptchaProvider, string> = {
  turnstile: 'https://challenges.cloudflare.com/turnstile/v0/api.js',
  hcaptcha: 'https://js.hcaptcha.com/1/api.js',
};

interface CaptchaApi {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
}

declare global {
  interface Window {
    turnstile?: CaptchaApi;
    hcaptcha?: CaptchaApi;
    [key: `__noamCaptchaOnload_${string}`]: (() => void) | undefined;
  }
}

// ⭐ קאש ברמת המודול — טעינה אחת בלבד לכל ספק, גם אם כמה מופעים של הרכיב
//    עולים (למשל HMR/StrictMode). ⛔ לא להוסיף תג `<script>` בכל mount.
const scriptLoadPromises = new Map<CaptchaProvider, Promise<void>>();

/** ⭐ ראו ההסבר בראש הקובץ — `onload=` ב-URL, ⛔ לא אירוע ה-`onload` של התג. */
function loadScript(provider: CaptchaProvider): Promise<void> {
  const cached = scriptLoadPromises.get(provider);
  if (cached) return cached;

  const promise = new Promise<void>((resolve, reject) => {
    const callbackName = `__noamCaptchaOnload_${provider}` as const;
    window[callbackName] = () => resolve();

    const script = document.createElement('script');
    script.src = `${SCRIPT_BASE[provider]}?onload=${callbackName}&render=explicit`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('טעינת סקריפט האימות האנושי נכשלה'));
    document.head.appendChild(script);
  });

  scriptLoadPromises.set(provider, promise);
  return promise;
}

export interface CaptchaProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
}

/** ⛔ `SITE_KEY` חסר (dev ללא מפתח אמיתי) → לא מרנדר כלום, ⛔ לא נופל. */
export function Captcha({ onVerify, onExpire }: CaptchaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onVerifyRef = useLatestRef(onVerify);
  const onExpireRef = useLatestRef(onExpire);

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return;
    let cancelled = false;
    const container = containerRef.current;

    loadScript(PROVIDER).then(
      () => {
        if (cancelled) return;
        const api = PROVIDER === 'hcaptcha' ? window.hcaptcha : window.turnstile;
        api?.render(container, {
          sitekey: SITE_KEY,
          // ⭐ קורא דרך ה-ref בזמן הקריאה בפועל — ⛔ לא "קופא" על ה-callback
          //    שהיה קיים ברגע ה-render() הראשוני
          callback: (token: string) => onVerifyRef.current(token),
          'expired-callback': () => onExpireRef.current?.(),
        });
      },
      () => {
        // ⚠️ כשל טעינה (רשת/חסימה) — הכפתור "שלח" נשאר מנוטרל בלי טוקן, וזו ההגנה עצמה
      },
    );

    return () => {
      cancelled = true;
    };
    // ⚠️ ⚠️ תלוי רק ב-SITE_KEY (קבוע מודול) — ⛔ בכוונה לא ב-onVerify/onExpire,
    //    ראו ההסבר בראש הקובץ. מפעיל את הווידג'ט פעם אחת לכל עלייה.
  }, []);

  if (!SITE_KEY) return null;

  return <div ref={containerRef} data-testid="captcha-widget" />;
}
